import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { BedDouble, CheckCircle2 } from "lucide-react";

import { hostelService, type Allocation, type Bed, type Hostel, type HostelFee, type Room, type StudentOption } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { allocationConfig, allocationStatus, currentBoardingFee, day, money, suitsResidence } from "./hostelLogic";

const learnerLine = (learner: StudentOption) => [learner.class_name, learner.student_id].filter(Boolean).join(" · ");

function PlaceForm({ learners, hostels, rooms, beds, fees, onClose, onDone }: {
  learners: StudentOption[];
  hostels: Hostel[];
  rooms: Room[];
  beds: Bed[];
  fees: HostelFee[];
  onClose: () => void;
  onDone: (row: Allocation) => void;
}) {
  const today = todayIso();
  const [learnerId, setLearnerId] = useState("");
  const [hostelId, setHostelId] = useState("");
  const [bedId, setBedId] = useState("");
  const [fee, setFee] = useState("");
  const learner = learners.find((row) => String(row.id) === learnerId);
  const open = hostels.filter((row) => row.is_active && row.current_occupancy < row.capacity && (!learner || suitsResidence(learner, row)));
  const hostel = open.find((row) => String(row.id) === hostelId);
  const roomsHere = rooms.filter((row) => row.hostel === hostel?.id);
  const freeBeds = beds.filter((bed) => bed.is_available && roomsHere.some((room) => room.id === bed.room));
  const bed = freeBeds.find((row) => String(row.id) === bedId);
  const save = useMutation({ mutationFn: hostelService.allocate, onSuccess: onDone });
  const pickHostel = (id: string) => {
    setHostelId(id);
    setBedId("");
    setFee(currentBoardingFee(fees, Number(id), today)?.amount ?? "");
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!bed || !hostel) return;
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      student_id: Number(learnerId),
      hostel_id: hostel.id,
      room_id: bed.room,
      bed_id: bed.id,
      start_date: text("start_date"),
      monthly_fee: fee || "0",
      security_deposit: text("deposit") ?? "0",
      emergency_contact_name: text("contact_name"),
      emergency_contact_phone: text("contact_phone"),
      emergency_contact_relationship: text("contact_relationship"),
      medical_conditions: text("medical"),
      dietary_restrictions: text("diet"),
      special_requirements: text("special"),
      notes: text("notes"),
    });
  };

  return (
    <FormModal
      id="boarding-place"
      title="Give a learner a bed"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The place could not be given.") : undefined}
      saveLabel="Hold the bed"
      note="The bed is held until the place is approved."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The learner" divided={false}>
        <Field label="Learner" as="div" className="sm:col-span-2" hint="Learners who already board are left out.">
          <SearchPick
            items={learners}
            value={learnerId}
            onChange={(value) => {
              setLearnerId(value);
              setHostelId("");
              setBedId("");
            }}
            label={(row) => row.full_name}
            detail={learnerLine}
            text={(row) => `${row.full_name} ${row.student_id}`}
            placeholder="Search by name, class or learner number"
            empty="No learner matches"
            required
          />
        </Field>
      </FormSection>

      <FormSection title="The bed">
        <Field label="Residence" hint={learner ? `Residences that take ${learner.gender === "female" ? "girls" : "boys"} and have room.` : "Choose the learner first."}>
          <select required value={hostelId} disabled={!learner} onChange={(event) => pickHostel(event.target.value)} className="input mt-1">
            <option value="">Choose a residence</option>
            {open.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.capacity - row.current_occupancy} free</option>)}
          </select>
        </Field>
        <Field label="Bed" hint={hostel && !freeBeds.length ? "No free bed is set up in this residence. Add beds under Rooms." : undefined}>
          <select required value={bedId} disabled={!hostel} onChange={(event) => setBedId(event.target.value)} className="input mt-1">
            <option value="">{hostel ? "Choose a bed" : "Choose the residence first"}</option>
            {roomsHere.map((room) => {
              const here = freeBeds.filter((row) => row.room === room.id);
              return here.length ? (
                <optgroup key={room.id} label={`Room ${room.room_number} (floor ${room.floor})`}>
                  {here.map((row) => <option key={row.id} value={row.id}>Bed {row.bed_number}</option>)}
                </optgroup>
              ) : null;
            })}
          </select>
        </Field>
        <Field label="Boarding from">
          <input required type="date" name="start_date" defaultValue={today} className="input mt-1" />
        </Field>
        <Field label="Boarding fee (UGX)" hint={hostel && currentBoardingFee(fees, hostel.id, today) ? "From the residence's fee list." : "No boarding fee is set for this residence yet."}>
          <input required type="number" min={0} step="any" value={fee} onChange={(event) => setFee(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Security deposit (UGX)" optional>
          <input type="number" name="deposit" min={0} step="any" className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="For the matron">
        <Field label="Who to call" optional>
          <input name="contact_name" maxLength={100} className="input mt-1" />
        </Field>
        <Field label="Phone" optional>
          <input type="tel" name="contact_phone" maxLength={20} className="input mt-1" placeholder="e.g. 0772 123456" />
        </Field>
        <Field label="Relationship" optional>
          <input name="contact_relationship" list="boarder-relationships" maxLength={50} className="input mt-1" />
          <datalist id="boarder-relationships">{["Mother", "Father", "Guardian", "Aunt", "Uncle", "Grandparent"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="Health" optional hint="e.g. asthma, inhaler kept by the matron.">
          <input name="medical" className="input mt-1" />
        </Field>
        <Field label="Diet" optional hint="e.g. no pork, vegetarian.">
          <input name="diet" className="input mt-1" />
        </Field>
        <Field label="Special needs" optional>
          <input name="special" className="input mt-1" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Who boards where, waiting approvals, and check-outs. */
export function BoardersPanel({ allocations, learners, hostels, rooms, beds, fees, isLoading, isError, onRetry, canManage, isFamily, onChanged }: {
  allocations?: Allocation[];
  learners: StudentOption[];
  hostels: Hostel[];
  rooms: Room[];
  beds: Bed[];
  fees: HostelFee[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  isFamily: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const approve = useMutation({ mutationFn: hostelService.approve, onSuccess: (row) => done(`${row.student_name} is boarding in ${row.hostel_name}, room ${row.room_number}.`) });
  const cancel = useMutation({ mutationFn: hostelService.cancel, onSuccess: (row) => done(`${row.student_name}'s place was cancelled; the bed is free again.`, true) });
  const checkout = useMutation({ mutationFn: hostelService.complete, onSuccess: (row) => done(`${row.student_name} has checked out; the bed is free again.`, true) });
  const classOf = new Map(learners.map((row) => [row.id, row.class_name ?? ""]));
  const boarding = new Set((allocations ?? []).filter((row) => ["pending", "active", "suspended"].includes(row.status)).map((row) => row.student));
  const free = learners
    .filter((row) => !boarding.has(row.id))
    .sort((a, b) => (a.class_level_order ?? 999) - (b.class_level_order ?? 999) || a.full_name.localeCompare(b.full_name));

  const columns: RegisterColumn<Allocation>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    ...(learners.length ? [{ header: "Class", cell: (row: Allocation) => classOf.get(row.student) || "—", exportValue: (row: Allocation) => classOf.get(row.student) ?? "", className: "text-gray-700" }] : []),
    { header: "Residence", cell: (row) => row.hostel_name, exportValue: (row) => row.hostel_name, className: "text-gray-700" },
    { header: "Room / bed", cell: (row) => `${row.room_number} · ${row.bed_number}`, exportValue: (row) => `${row.room_number} ${row.bed_number}`, className: "whitespace-nowrap text-gray-700" },
    { header: "Since", cell: (row) => day(row.start_date), exportValue: (row) => day(row.start_date), className: "tabular-nums text-gray-700" },
    { header: "Contact", cell: (row) => row.emergency_contact_phone || "—", exportValue: (row) => [row.emergency_contact_name, row.emergency_contact_phone].filter(Boolean).join(" "), className: "text-gray-700" },
    { header: "Health", cell: (row) => (row.medical_conditions ? <span className="text-amber-800">Note</span> : ""), exportValue: (row) => row.medical_conditions ?? "", exportOnly: isFamily },
    { header: "Status", cell: (row) => <StatusDot {...allocationStatus(row)} />, exportValue: (row) => allocationStatus(row).label },
  ];

  const row = viewing !== null ? allocations?.find((entry) => entry.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={allocations ? "loaded" : "loading"}
        title={isFamily ? "Boarding place" : "Boarders"}
        description={canManage ? "Open a place to approve it, cancel it or check the learner out." : isFamily ? "Where your child sleeps and who the matron calls." : "Who boards where, and who to call."}
        actions={canManage && (
          <button type="button" disabled={!hostels.some((row) => row.is_active)} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <BedDouble className="mr-1.5 h-3.5 w-3.5" /> Give a bed
          </button>
        )}
        rows={allocations}
        config={allocationConfig}
        columns={columns}
        rowKey={(entry) => entry.id}
        onOpen={(entry) => {
          [approve, cancel, checkout].forEach((mutation) => mutation.reset());
          setViewing(entry.id);
        }}
        openLabel={(entry) => (canManage && entry.status === "pending" ? "Approve" : "Open")}
        noun={["place", "places"]}
        exportName={{ file: "boarders", title: "Boarders" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "Nobody boards yet. Give the first learner a bed." : "No boarding place."}
        canExport={!isFamily}
        initialState={(allocations ?? []).some((entry) => entry.status === "active") ? undefined : { view: "all" }}
      />

      {row && (
        <DetailsModal
          title={`${row.student_name} · ${row.hostel_name}`}
          headline={{ label: "Sleeps in", value: `Room ${row.room_number}, bed ${row.bed_number}`, note: `${row.hostel_name} · since ${day(row.start_date)}` }}
          status={allocationStatus(row)}
          facts={[
            ["Learner number", row.student_id],
            ["Class", classOf.get(row.student) || undefined],
            ["Who to call", [row.emergency_contact_name, row.emergency_contact_relationship && `(${row.emergency_contact_relationship})`].filter(Boolean).join(" ") || undefined],
            ["Phone", row.emergency_contact_phone ? <a key="phone" href={`tel:${row.emergency_contact_phone}`} className="text-primary-700 hover:underline">{row.emergency_contact_phone}</a> : undefined],
            ["Boarding fee", money(row.monthly_fee)],
            ["Security deposit", Number(row.security_deposit) ? `${money(row.security_deposit)}${row.deposit_paid ? " (paid)" : ""}` : undefined],
            ["Given by", row.allocated_by_name],
            ["Approved by", row.approved_by_name],
            ["Ended", day(row.end_date) || undefined],
          ]}
          sections={[
            { title: "Health", body: row.medical_conditions, tone: "warning" },
            { title: "Diet", body: row.dietary_restrictions },
            { title: "Special needs", body: row.special_requirements },
            { title: "Notes", body: row.notes },
            { title: row.status === "cancelled" ? "Why it was cancelled" : "Why they left", body: row.end_reason },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && row.status === "pending" && (
                <button type="button" disabled={approve.isPending} onClick={() => approve.mutate(row.id)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Approve the place
                </button>
              )}
            </>
          }
        >
          {approve.isError && errorLine(approve.error, "The place could not be approved.")}
          {canManage && row.status === "active" && (
            <ReasonPanel
              title="Check out"
              description="The learner leaves the residence today and the bed is freed."
              placeholder="e.g. Became a day scholar from next term."
              context={{ key: "hostel.note", params: { kind: "checkout", id: row.id } }}
              button="Check out"
              danger
              busy={checkout.isPending}
              error={checkout.error}
              errorText="The learner could not be checked out."
              onSubmit={(reason) => checkout.mutate({ id: row.id, reason })}
            />
          )}
          {canManage && (row.status === "pending" || row.status === "suspended") && (
            <ReasonPanel
              title="Cancel the place"
              description="The held bed is freed."
              placeholder="e.g. The family chose day school instead."
              context={{ key: "hostel.note", params: { kind: "allocation_cancel", id: row.id } }}
              button="Cancel the place"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The place could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: row.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {adding && (
        <PlaceForm
          learners={free}
          hostels={hostels}
          rooms={rooms}
          beds={beds}
          fees={fees}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            done(`Bed ${saved.bed_number} in room ${saved.room_number} is held for ${saved.student_name}, waiting for approval.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
